import React, { useState, useEffect, useRef } from "react";
import { Box, Button, FormControl, InputLabel, MenuItem, Modal, Typography, Select, Snackbar, CircularProgress, TextField, Grid } from "@mui/material";
import MuiAlert from "@mui/material/Alert";
import { Autocomplete, Paper, Popper } from "@mui/material";
import { systemsIntegrationAxios as axios } from "../../../utils/AxiosUtility";


export default function EditInternalInfo({ internInfo, onUpdateAll, updateTz, prog, ft, cstation, tt, tzone, csId }) {
  const [open, setOpen] = useState(false);
  const [loading, setLoading] = useState(false);
  const [snackbarOpen, setSnackbarOpen] = useState(false);
  const [sprocExecuted, setSprocExecuted] = useState(false);
  
  const [programmer, setProgrammer] = useState(prog || "");
  const [installTester, setInstallTester] = useState(ft || "");
  const [centralStation, setCentralStation] = useState(cstation || "");
  const [timeZone, setTimeZone] = useState(tzone || "");
  const [timerTest, setTimerTest] = useState(tt || "");
  const [searchText, setSearchText] = useState(tzone || "");
  const [options, setOptions] = useState([]);
  const [isDropdownOpen, setIsDropdownOpen] = useState(false);
  const timeoutRef = useRef(null);

  const handleOpen = () => setOpen(true);
  const handleClose = () => setOpen(false);
  const handleSnackbarClose = () => setSnackbarOpen(false);

  const programmerOptions = ["Adam Derose", "Kim Kluse", "Sean McGill"];
  const centralStationOptions = ["COPS", "Acadian"];

  useEffect(() => {
    setProgrammer(prog || "");
    setInstallTester(ft || "");
    setCentralStation(cstation || "");
    setTimerTest(tt || "");
    setTimeZone(tzone || "");
  }, [prog, ft, cstation, tt, tzone]);

  const searchTimezones = async (text) => {
    try {
      const response = await axios.get(`api/InternalInfo/searchTimezones`, {
        params: { searchText: text }
      });
      setOptions(response.data);
    }
    catch (error) {
      alert(`Error fetching timezones`);
      setOptions([]);
    }
  };

  const executeSproc = () => {
      try {
          const storedProcEndpoint = `api/ProductSummary/updateProductSummary`;
          axios.get(storedProcEndpoint);

          setSprocExecuted(true);
      }
      catch (error) {
          alert("Error");
      }
  }

  useEffect(() => {
    if (timeoutRef.current) {
      clearTimeout(timeoutRef.current);
    }

    if (searchText.length > 0) {
      timeoutRef.current = setTimeout(() => {
        searchTimezones(searchText);
      }, 300);
    }
    else {
      setOptions([]);
    }

    return () => {
      if (timeoutRef.current) {
        clearTimeout(timeoutRef.current);
      }
    };
  }, [searchText]);

  const saveInternalInfo = async () => {
    const newInternalInfo = {
      productConfigurationId: internInfo.productConfigurationId,
      programmer,
      installTester,
      timerTest,
    };
  
    const newSiteInfo = {
      csId: internInfo.csId,
      centralStation,
    };

    const newTimezoneInfo = {
      productId: internInfo.productId,
      unitTimeZone: timeZone
    }
  
    setLoading(true);
  
    try {
      await Promise.all([
        axios.put("api/InternalInfo/updateInternalInfo", newInternalInfo),
        axios.put("api/InternalInfo/updateInternalSiteInfo", newSiteInfo),
        axios.put("api/InternalInfo/updateTimezone", newTimezoneInfo)
      ]);
  
      setSnackbarOpen(true);
      
      await executeSproc();
      await onUpdateAll();
      await updateTz(internInfo.productId);
      
      handleClose();
    } catch (error) {
      alert("Error saving info");
      alert(error.response?.data || "Error saving information. Please try again.");
    } finally {
      setLoading(false);
    }
  };

  const renderTimezoneAutocomplete = () => (
    <Autocomplete
      value={timeZone}
      onChange={(event, newValue) => {
        setTimeZone(newValue);
      }}
      inputValue={searchText}
      onInputChange={(event, newInputValue) => {
        setSearchText(newInputValue);
        setIsDropdownOpen(true);
      }}
      options={options}
      renderInput={(params) => (
        <TextField
          {...params}
          label="Time Zone"
          fullWidth
          sx={{
            marginBottom: '20px',
            "& .MuiInputBase-root": {
              fontFamily: "Roboto Mono, monospace",
            },
            "& .MuiInputLabel-root": {
              fontFamily: "Roboto Mono, monospace",
            },
            width: "300%"
          }}
        />
      )}
      filterOptions={(options, { inputValue }) => {
        const inputValueLower = inputValue.toLowerCase();
        return options.filter(option => option.toLowerCase().startsWith(inputValueLower));
      }}
      noOptionsText="[No Matching Time Zones]"
      disablePortal={false}
      selectOnFocus
      clearOnBlur
      handleHomeEndKeys
    />
  );

  return (
    <>
      <Button 
        variant="contained" 
        color="primary" 
        style={{ fontFamily: "'Roboto Mono', monospace", display: 'table-header-group', marginLeft: 'auto', marginTop: '15%', marginBottom: '15%' }} 
        onClick={handleOpen}
      >
        Edit
      </Button>
      <Modal
        open={open}
        onClose={handleClose}
        aria-labelledby="modal-modal-title"
        aria-describedby="modal-modal-description"
      >
        <Box
          sx={{
            position: "absolute",
            top: "50%",
            left: "50%",
            transform: "translate(-50%, -50%)",
            width: 600,
            height: "80vh",
            maxHeight: '435px',
            overflowY: "auto",
            bgcolor: "background.paper",
            border: "2px solid #000",
            boxShadow: 24,
            p: 4,
          }}
        >
          <Button
            variant="outlined"
            onClick={handleClose}
            style={{ position: "absolute", top: "10px", right: "10px" }}
            disabled={loading}
          >
            X
          </Button>

          <Typography variant="h6" component="h2" style={{ fontFamily: "'Roboto Mono', monospace", paddingBottom: "20px" }}>
            Edit Internal Info
          </Typography>

          <Grid container rowSpacing={0} style={{ padding: "30px" }}>
            <Grid item xs={6} container justifyContent="center">
              <FormControl fullWidth style={{ marginBottom: '20px', marginRight: '5%' }}>
                <InputLabel id="programmer-label" sx={{ fontFamily: "'Roboto Mono', monospace"}}>Programmer</InputLabel>
                <Select
                  labelId="programmer-label"
                  value={programmer}
                  onChange={(e) => setProgrammer(e.target.value)}
                  label="Programmer"
                >
                  {programmerOptions.map((option, index) => (
                    <MenuItem key={index} value={option}>
                      {option}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Grid>
            <Grid item xs={6} container justifyContent="center">
              <TextField
                fullWidth
                label="Install Tester"
                value={installTester}
                onChange={(e) => setInstallTester(e.target.value)}
                style={{ marginBottom: "20px", marginLeft: '5%' }}
                sx={{
                  "& .MuiInputBase-root": {
                    fontFamily: "Roboto Mono, monospace",
                  },
                  "& .MuiInputLabel-root": {
                    fontFamily: "Roboto Mono, monospace",
                  },
                }}
              />
            </Grid>
            <Grid item xs={6} container justifyContent="center">
              <FormControl fullWidth style={{ marginBottom: '20px', marginRight: '5%' }}>
                <InputLabel id="central-station-label" sx={{ fontFamily: "'Roboto Mono', monospace"}}>Central Station</InputLabel>
                <Select
                  labelId="central-station-label"
                  value={centralStation}
                  onChange={(e) => setCentralStation(e.target.value)}
                  label="Central Station"
                >
                  {centralStationOptions.map((option, index) => (
                    <MenuItem key={index} value={option}>
                      {option}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Grid>
            <Grid item xs={6} container justifyContent="center">
              <TextField
                fullWidth
                label="Timer Test"
                value={timerTest}
                onChange={(e) => setTimerTest(e.target.value)}
                style={{ marginBottom: "20px", marginLeft: '5%' }}
                sx={{
                  "& .MuiInputBase-root": {
                    fontFamily: "Roboto Mono, monospace",
                  },
                  "& .MuiInputLabel-root": {
                    fontFamily: "Roboto Mono, monospace",
                  },
                }}
              />
            </Grid>
            <Grid item xs={6} container justifyContent="center">
              {renderTimezoneAutocomplete()}
            </Grid>
          </Grid>

          <div style={{ position: "absolute", right: "10px" }}>
            {loading && <CircularProgress />}
            <Button
              style={{ color: "green", fontFamily: "'Roboto Mono', monospace" }}
              onClick={saveInternalInfo}
              disabled={loading}
            >
              Save
            </Button>
          </div>
        </Box>
      </Modal>
      <Snackbar
        open={snackbarOpen}
        autoHideDuration={3000}
        onClose={handleSnackbarClose}
      >
        <MuiAlert
          onClose={handleSnackbarClose}
          severity="success"
          sx={{ width: "100%" }}
        >
          Internal Info updated!
        </MuiAlert>
      </Snackbar>
    </>
  );
}
